import React from 'react';
import type { ScopeTreeEntry } from './ScopeTreeView';
import type { EditableStakeholder } from './ScopeStakeholderList';

interface ScopePublishDiffModalProps {
  open: boolean;
  scopeName: string;
  fromVersion: number;
  isTenantDefault: boolean;
  baseline: ScopeTreeEntry[];
  current: ScopeTreeEntry[];
  stakeholderBaseline: EditableStakeholder[];
  stakeholderCurrent: EditableStakeholder[];
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

interface DiffGroup {
  label: string;
  rows: Array<{ key: string; primary: string; secondary?: string }>;
  tone: 'success' | 'warning' | 'info' | 'danger';
}

const stakeholderLabel = (binding: EditableStakeholder, titleByKey: Map<string, string>): string => {
  const target = binding.scopeEntryKey
    ? `on "${titleByKey.get(binding.scopeEntryKey) ?? '(entry)'}"`
    : 'scope-wide';
  const name = binding.displayName || binding.stakeholderRef;
  return `${name} ${target}`;
};

const buildDiff = (
  baseline: ScopeTreeEntry[],
  current: ScopeTreeEntry[],
  stakeholderBaseline: EditableStakeholder[],
  stakeholderCurrent: EditableStakeholder[]
): DiffGroup[] => {
  const baseByKey = new Map(baseline.map((entry) => [entry.scopeEntryKey, entry]));
  const currentByKey = new Map(current.map((entry) => [entry.scopeEntryKey, entry]));

  const added = current.filter((entry) => entry.__dirty === 'new');
  const renamed = current.filter((entry) => entry.__dirty === 'renamed');
  const moved = current.filter((entry) => entry.__dirty === 'moved');
  const retired = current.filter((entry) => entry.__dirty === 'retired');
  const restored = current.filter((entry) => entry.__dirty === 'restored');

  const titleOf = (key: string | null): string => {
    if (!key) return 'Top level';
    return currentByKey.get(key)?.title || baseByKey.get(key)?.title || '(unknown)';
  };

  const groups: DiffGroup[] = [];

  if (added.length > 0) {
    groups.push({
      label: `Added (${added.length})`,
      tone: 'success',
      rows: added.map((entry) => ({
        key: entry.scopeEntryKey,
        primary: entry.title || '(untitled)',
        secondary: `Under: ${titleOf(entry.parentScopeEntryKey)}`
      }))
    });
  }

  if (renamed.length > 0) {
    groups.push({
      label: `Renamed (${renamed.length})`,
      tone: 'warning',
      rows: renamed.map((entry) => {
        const previous = baseByKey.get(entry.scopeEntryKey);
        return {
          key: entry.scopeEntryKey,
          primary: `${previous?.title ?? '(unknown)'} → ${entry.title || '(untitled)'}`
        };
      })
    });
  }

  if (moved.length > 0) {
    groups.push({
      label: `Moved (${moved.length})`,
      tone: 'info',
      rows: moved.map((entry) => {
        const previous = baseByKey.get(entry.scopeEntryKey);
        const fromParent = previous ? titleOf(previous.parentScopeEntryKey) : '(unknown)';
        const toParent = titleOf(entry.parentScopeEntryKey);
        const sameParent = (previous?.parentScopeEntryKey ?? null) === (entry.parentScopeEntryKey ?? null);
        return {
          key: entry.scopeEntryKey,
          primary: entry.title || '(untitled)',
          secondary: sameParent ? `Reordered under ${toParent}` : `${fromParent} → ${toParent}`
        };
      })
    });
  }

  if (retired.length > 0) {
    groups.push({
      label: `Retired (${retired.length})`,
      tone: 'danger',
      rows: retired.map((entry) => ({
        key: entry.scopeEntryKey,
        primary: entry.title || '(untitled)',
        secondary: 'Hidden from new audits. Existing references remain valid.'
      }))
    });
  }

  if (restored.length > 0) {
    groups.push({
      label: `Restored (${restored.length})`,
      tone: 'info',
      rows: restored.map((entry) => ({
        key: entry.scopeEntryKey,
        primary: entry.title || '(untitled)'
      }))
    });
  }

  const titleByKey = new Map<string, string>();
  current.forEach((entry) => titleByKey.set(entry.scopeEntryKey, entry.title));
  baseline.forEach((entry) => {
    if (!titleByKey.has(entry.scopeEntryKey)) {
      titleByKey.set(entry.scopeEntryKey, entry.title);
    }
  });
  const stakeholderBaselineById = new Map(stakeholderBaseline.map((b) => [b.__localId, b]));

  const stakeholdersAdded = stakeholderCurrent.filter((binding) => binding.__dirty === 'new');
  const stakeholdersEdited = stakeholderCurrent.filter((binding) => binding.__dirty === 'edited');
  const stakeholdersRemoved = stakeholderCurrent.filter((binding) => binding.__dirty === 'removed');

  if (stakeholdersAdded.length > 0) {
    groups.push({
      label: `Stakeholders added (${stakeholdersAdded.length})`,
      tone: 'success',
      rows: stakeholdersAdded.map((binding) => ({
        key: `add:${binding.__localId}`,
        primary: stakeholderLabel(binding, titleByKey),
        secondary: binding.inheritsToChildren ? 'Inherits to descendants' : undefined
      }))
    });
  }

  if (stakeholdersEdited.length > 0) {
    groups.push({
      label: `Stakeholders changed (${stakeholdersEdited.length})`,
      tone: 'warning',
      rows: stakeholdersEdited.map((binding) => {
        const previous = stakeholderBaselineById.get(binding.__localId);
        const changes: string[] = [];
        if (previous && previous.bindingMode !== binding.bindingMode) {
          changes.push(`use as: ${previous.bindingMode} → ${binding.bindingMode}`);
        }
        if (previous && previous.inheritsToChildren !== binding.inheritsToChildren) {
          changes.push(binding.inheritsToChildren ? 'now inherits' : 'no longer inherits');
        }
        if (previous && (previous.displayName ?? '') !== (binding.displayName ?? '')) {
          changes.push('display name');
        }
        return {
          key: `edit:${binding.__localId}`,
          primary: stakeholderLabel(binding, titleByKey),
          secondary: changes.join(', ') || undefined
        };
      })
    });
  }

  if (stakeholdersRemoved.length > 0) {
    groups.push({
      label: `Stakeholders removed (${stakeholdersRemoved.length})`,
      tone: 'danger',
      rows: stakeholdersRemoved.map((binding) => ({
        key: `del:${binding.__localId}`,
        primary: stakeholderLabel(binding, titleByKey)
      }))
    });
  }

  return groups;
};

/**
 * Preview of what is about to be published. Shows the local diff
 * between the baseline (the active published Scope) and the working
 * copy. Replaces the previous window.confirm with a structured
 * diff so the admin can scan exactly what changes ship.
 *
 * The diff is computed client-side from the dirty markers and the
 * stored baseline — no server query. That's good for UAT and the
 * cases the admin actually changes interactively. It will not show
 * a server-blessed diff against a stale baseline, but that's not
 * what the admin is asking when they hit "Save changes".
 */
export const ScopePublishDiffModal: React.FC<ScopePublishDiffModalProps> = ({
  open,
  scopeName,
  fromVersion,
  isTenantDefault,
  baseline,
  current,
  stakeholderBaseline,
  stakeholderCurrent,
  saving,
  onCancel,
  onConfirm
}) => {
  const groups = React.useMemo(
    () => buildDiff(baseline, current, stakeholderBaseline, stakeholderCurrent),
    [baseline, current, stakeholderBaseline, stakeholderCurrent]
  );
  const dirtyCount = groups.reduce((acc, group) => acc + group.rows.length, 0);

  React.useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="scopes-modal-backdrop" role="presentation" onClick={(event) => {
      if (event.target === event.currentTarget && !saving) onCancel();
    }}>
      <div className="scopes-modal" role="dialog" aria-labelledby="scope-publish-diff-title" aria-modal="true">
        <div className="scopes-modal-header">
          <h3 id="scope-publish-diff-title">Publish v{fromVersion + 1} of "{scopeName}"</h3>
          <p className="muted">
            {dirtyCount === 0
              ? 'No changes to publish.'
              : `${dirtyCount} change${dirtyCount === 1 ? '' : 's'} will become v${fromVersion + 1}.`}
            {isTenantDefault && ' This Scope is the tenant default; the new version will take its place.'}
          </p>
        </div>
        <div className="scopes-modal-body">
          {groups.length === 0 ? (
            <div className="scopes-modal-empty">Nothing to publish — the working copy matches the active version.</div>
          ) : (
            groups.map((group) => (
              <section key={group.label} className={`scopes-diff-group tone-${group.tone}`}>
                <h4>{group.label}</h4>
                <ul>
                  {group.rows.map((row) => (
                    <li key={row.key}>
                      <span className="primary">{row.primary}</span>
                      {row.secondary && <span className="secondary">{row.secondary}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
        <div className="scopes-modal-footer">
          <button className="btn secondary" type="button" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn"
            type="button"
            onClick={onConfirm}
            disabled={saving || dirtyCount === 0}
          >
            {saving ? 'Publishing...' : `Publish v${fromVersion + 1}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScopePublishDiffModal;
